<template>
    <div>
        <div
            v-if="commitment.mode === 'document' && commitment.document"
            class="mb-4">
            <a
                v-if="commitment.document.url"
                :href="commitment.document.url"
                class="d-inline-flex align-items-center gap-2 font-weight-bold"
                target="_blank"
                rel="noopener">
                <font-awesome-icon icon="file" />
                <span>{{ documentName }}</span>
            </a>
            <p
                v-else
                class="text-muted mb-0">
                {{ $t('eventRegistration.commitment.documentUnavailable') }}
            </p>
        </div>
        <div
            v-else-if="commitment.mode === 'text'"
            class="commitment-text mb-4"
            v-html="commitment.text" />

        <base-input
            :name="name"
            hide-label
            no-margin
            required
            :validation-label="label || $t('eventRegistration.commitment.stepSignature')"
            :model-value="completionValue">
            <div>
                <section>
                    <h6 class="d-flex align-items-center gap-2 mb-2">
                        <Badge
                            v-if="commitment.requireSmsVerification"
                            circle
                            size="md"
                            type="primary"
                            class="flex-shrink-0">
                            1
                        </Badge>
                        {{ $t('eventRegistration.commitment.signature') }}
                    </h6>
                    <div v-if="signedOnPhone">
                        <img
                            :src="signature"
                            :alt="$t('eventRegistration.commitment.signature')"
                            class="signature-image w-100 border rounded bg-white">
                        <div class="d-flex justify-content-between align-items-center gap-2 mt-2">
                            <span class="badge badge-pill badge-success">
                                {{ $t('eventRegistration.commitment.handoff.signedOnPhone') }}
                            </span>
                            <base-button
                                type="link"
                                size="sm"
                                class="shadow-none"
                                @click="discardPhoneSignature">
                                {{ $t('eventRegistration.commitment.handoff.discard') }}
                            </base-button>
                        </div>
                    </div>
                    <CommitmentHandoff
                        v-else-if="handoffMode"
                        :event-id="eventData.id"
                        :question-uuid="question.uuid"
                        :require-sms="commitment.requireSmsVerification"
                        :phone="phone"
                        :phone-valid="phoneLooksValid"
                        :name="name"
                        @update:phone="setPhone"
                        @signed="takePhoneSignature"
                        @cancel="handoffMode = false" />
                    <template v-else>
                        <SignaturePad
                            v-model="signature"
                            :locked="verified" />
                        <small
                            v-if="verified"
                            class="text-muted d-block mt-1">
                            {{ $t('eventRegistration.commitment.signatureLockedHint') }}
                        </small>
                        <!-- type="" suppresses the default btn-default, which would render this link filled -->
                        <base-button
                            v-else
                            link
                            size="sm"
                            type=""
                            class="px-0 shadow-none"
                            @click="startHandoff">
                            <font-awesome-icon
                                icon="mobile-screen"
                                class="mr-1" />
                            {{ $t('eventRegistration.commitment.handoff.signOnPhone') }}
                        </base-button>
                    </template>
                </section>

                <section
                    v-if="commitment.requireSmsVerification && !handoffMode && !signedOnPhone"
                    class="mt-4"
                    :class="{ 'opacity-6': !hasSignature && !verified }">
                    <h6 class="d-flex align-items-center gap-2 mb-2">
                        <Badge
                            circle
                            size="md"
                            type="primary"
                            class="flex-shrink-0">
                            2
                        </Badge>
                        {{ $t('eventRegistration.commitment.stepSmsConfirmation') }}
                    </h6>

                    <p
                        v-if="!hasSignature && !verified"
                        class="text-muted mb-0">
                        {{ $t('eventRegistration.commitment.signFirstHint') }}
                    </p>

                    <template v-else>
                        <label class="d-block font-weight-bold mb-1">{{ $t('eventRegistration.commitment.mobileNumber') }}</label>
                        <div class="d-flex flex-column flex-sm-row gap-2">
                            <base-input
                                :model-value="phone"
                                :name="name + ' :: phone'"
                                class="flex-grow-1 mb-0"
                                hide-label
                                type="tel"
                                :disabled="verified"
                                :placeholder="$t('eventRegistration.commitment.mobileNumber')"
                                @update:model-value="setPhone" />
                            <base-button
                                v-if="!codeSent && !verified"
                                type="primary"
                                outline
                                no-wrap
                                class="flex-shrink-0 align-self-start"
                                :loading="sending"
                                :disabled="!phoneLooksValid || sending"
                                @click="sendCode">
                                {{ $t('eventRegistration.commitment.confirmViaSms') }}
                            </base-button>
                        </div>
                        <small
                            v-if="phone && !phoneLooksValid && !verified"
                            class="text-danger d-block mt-1">
                            {{ $t('eventRegistration.commitment.mobileInvalid') }}
                        </small>
                        <small
                            v-if="sendError"
                            class="text-danger d-block mt-1">
                            {{ sendError }}
                        </small>

                        <div
                            v-if="codeSent && !verified"
                            class="mt-3">
                            <small class="text-muted d-block mb-1">
                                {{ $t('eventRegistration.commitment.codeSentTo', { number: maskedNumber }) }}
                            </small>
                            <label class="d-block font-weight-bold mb-1">{{ $t('eventRegistration.commitment.code') }}</label>
                            <div class="d-flex flex-column flex-sm-row gap-2">
                                <base-input
                                    v-model="code"
                                    :name="name + ' :: code'"
                                    class="flex-grow-1 mb-0"
                                    hide-label
                                    :placeholder="$t('eventRegistration.commitment.code')" />
                                <base-button
                                    type="primary"
                                    no-wrap
                                    class="flex-shrink-0 align-self-start"
                                    :loading="verifying"
                                    :disabled="!code || verifying"
                                    @click="verifyCode">
                                    {{ $t('eventRegistration.commitment.verify') }}
                                </base-button>
                            </div>
                            <small
                                v-if="verifyError"
                                class="text-danger d-block mt-1">
                                {{ verifyError }}
                            </small>
                            <!-- type="" suppresses the default btn-default, which would render this link filled -->
                            <base-button
                                link
                                size="sm"
                                type=""
                                class="px-0 mt-1 shadow-none"
                                :disabled="resendSeconds > 0 || sending"
                                @click="sendCode">
                                {{
                                    resendSeconds > 0
                                        ? $t('eventRegistration.commitment.resendCodeIn', { seconds: resendSeconds })
                                        : $t('eventRegistration.commitment.resendCode')
                                }}
                            </base-button>
                        </div>

                        <div
                            v-if="verified"
                            class="mt-2 text-success">
                            <font-awesome-icon icon="check" />
                            {{ $t('eventRegistration.commitment.verified') }}
                        </div>
                    </template>
                </section>
            </div>
        </base-input>
    </div>
</template>

<script>
import SignaturePad from "@frontend/app/components/SignaturePad";
import CommitmentHandoff from "./CommitmentHandoff";
import mainRegistrationMixin from "@/mixins/registration/mainRegistrationMixin";
import {commitmentErrorMessage} from "@/util/commitmentErrorMessage";

export default {
    name: "CommitmentAnswer",
    components: {SignaturePad, CommitmentHandoff},
    mixins: [mainRegistrationMixin],
    props: {
        question: {
            type: Object,
            required: true
        },
        personKey: {
            type: [String, Number],
            default: null
        },
        name: {
            type: String,
            default: ''
        },
        label: {
            type: String,
            default: ''
        }
    },
    emits: ['update'],
    data() {
        return {
            signature: '',
            phone: '',
            phoneEdited: false,
            code: '',
            verificationToken: null,
            signedToken: null,
            maskedNumber: '',
            codeSent: false,
            verified: false,
            sending: false,
            verifying: false,
            sendError: '',
            verifyError: '',
            resendSeconds: 0,
            resendTimer: null,
            handoffMode: false,
            signedOnPhone: false
        }
    },
    computed: {
        commitment() {
            return this.question.commitment ?? {mode: 'text', requireSmsVerification: true}
        },
        documentName() {
            return this.commitment.document?.name || this.$t('eventRegistration.commitment.document')
        },
        hasSignature() {
            return !!this.signature
        },
        phoneLooksValid() {
            return this.looksLikePhone(this.phone)
        },
        isComplete() {
            if (!this.signature) return false
            if (this.commitment.requireSmsVerification) return this.verified && !!this.signedToken
            return true
        },
        completionValue() {
            return this.isComplete ? 'signed' : ''
        },
        prefillPhone() {
            const persons = this.registrationData?.persons ?? {}
            const participating = this.registrationData?.participatingPersons ?? []

            // Participant question: personKey is the person's UUID. A registrar question has no person
            // 'registrar'; there the one stored as registrar/invoice recipient counts.
            const personKey = this.personKey && this.personKey !== 'registrar'
                ? this.personKey
                : this.registrationData?.invoiceReceivingPerson

            return [
                persons[personKey]?.phone,
                participating.length === 1 ? persons[participating[0]]?.phone : null
            ].find(candidate => this.looksLikePhone(candidate)) ?? ''
        }
    },
    methods: {
        setPhone(value) {
            this.phoneEdited = true
            this.phone = value
        },
        // Also gates the prefill: an unusable number from the person data would otherwise land in the
        // field unasked and greet the registrant with a validation error.
        looksLikePhone(value) {
            if (typeof value !== 'string' || !value) return false
            return value.replace(/\D/g, '').length >= 7 && /^[+\d][\d\s/()-]*$/.test(value.trim())
        },
        errorMessage(error, fallbackKey) {
            return commitmentErrorMessage(this.$t, error, fallbackKey)
        },
        // A signature drawn here before must not count while the phone is asked for one.
        startHandoff() {
            this.signature = ''
            this.handoffMode = true
        },
        // The phone confirmed the number this form named, so the answer is complete as it stands.
        takePhoneSignature({signature, verificationToken}) {
            this.handoffMode = false
            this.signedOnPhone = true
            this.signature = signature
            this.signedToken = verificationToken
            this.verified = !!verificationToken
        },
        discardPhoneSignature() {
            this.signedOnPhone = false
            this.signature = ''
            this.signedToken = null
            this.verified = false
        },
        async sendCode() {
            this.sending = true
            this.sendError = ''
            this.verifyError = ''
            try {
                const result = await this.$axios.post(
                    this.$apiRoute('api.registration.veranstaltung.commitment.sendCode', {veranstaltung: this.eventData.id}),
                    {phone: this.phone, questionUuid: this.question.uuid}
                )
                this.verificationToken = result.data.verificationToken
                this.maskedNumber = result.data.maskedNumber
                this.code = ''
                this.codeSent = true
                this.startResendCountdown()
            } catch (e) {
                this.sendError = this.errorMessage(e, 'sendFailed')
            }
            this.sending = false
        },
        async verifyCode() {
            this.verifying = true
            this.verifyError = ''
            try {
                const result = await this.$axios.post(
                    this.$apiRoute('api.registration.veranstaltung.commitment.verifyCode', {veranstaltung: this.eventData.id}),
                    {verificationToken: this.verificationToken, code: this.code}
                )
                this.signedToken = result.data.signedToken
                this.verified = true
                this.clearResendCountdown()
            } catch (e) {
                this.verifyError = this.errorMessage(e, 'codeInvalid')
            }
            this.verifying = false
        },
        startResendCountdown() {
            this.clearResendCountdown()
            this.resendSeconds = 60
            this.resendTimer = setInterval(() => {
                if (this.resendSeconds <= 1) {
                    this.clearResendCountdown()
                    return
                }
                this.resendSeconds--
            }, 1000)
        },
        clearResendCountdown() {
            if (this.resendTimer) {
                clearInterval(this.resendTimer)
                this.resendTimer = null
            }
            this.resendSeconds = 0
        },
        emitAnswer() {
            if (!this.isComplete) {
                this.$emit('update', [])
                return
            }
            this.$emit('update', [{
                signature: this.signature,
                phone: this.phone,
                verificationToken: this.signedToken
            }])
        }
    },
    beforeUnmount() {
        this.clearResendCountdown()
    },
    watch: {
        prefillPhone: {
            immediate: true,
            handler(value) {
                // The person data is typed in parallel, so follow its current state instead of snapshotting
                // once. Otherwise the number sticks at the intermediate state it first became usable at.
                // As soon as the registrant types here, a code went out or the phone took over, only the
                // field itself counts: a hand-off fixes the number it was created with.
                if (this.phoneEdited || this.codeSent || this.verified || this.handoffMode || this.signedOnPhone) return
                this.phone = value
            }
        },
        signature() {
            this.emitAnswer()
        },
        signedToken() {
            this.emitAnswer()
        }
    }
}
</script>

<style lang="scss" scoped>
// The commitment text arrives as editor HTML; only its trailing paragraph margin has to go so the
// gap to step 1 stays the container's.
.commitment-text :deep(> :last-child) {
    margin-bottom: 0;
}

// same box as the signature pad, so taking the signature over from the phone does not shift the form
.signature-image {
    height: 200px;
    object-fit: contain;
}
</style>
